<script>
  import {onMount, onDestroy} from 'svelte'
  import {media} from 'svelte-match-media'
  import gsap from 'gsap'
  import ScrollTrigger from 'gsap/ScrollTrigger'

  import LogoCW from '../svgs/CW.svelte'
  import LogoDish from '../svgs/Dish.svelte'
  import LogoHulu from '../svgs/Hulu.svelte'
  import LogoPluto from '../svgs/Pluto.svelte'
  import LogoSling from '../svgs/Sling.svelte'
  import LogoFox from '../svgs/Fox.svelte'
  import LogoNBC from '../svgs/NBC.svelte'
  import LogoAMC from '../svgs/AMC.svelte'
  import LogoAE from '../svgs/AE.svelte'
  import LogoDisney from '../svgs/Disney.svelte'
  import LogoTubi from '../svgs/Tubi.svelte'
  import LogoUnivision from '../svgs/Univision.svelte'
  import LogoViacom from '../svgs/Viacom.svelte'
  import LogoESPN from '../svgs/ESPN.svelte'
  import LogoXumo from '../svgs/Xumo.svelte'
  import AnimatedTitle from '../components/AnimatedTitle.svelte'
  import ViewSource from '../components/ViewSource.svelte'
  import AnimatedBlock from '../components/AnimatedBlock.svelte'

  let logos, st

  onMount(() => {
    const timelines = []
    const svgs = logos.querySelectorAll('.logo-square')
    svgs.forEach((svg, index) => {
      const tl = gsap.timeline({repeat: -1, paused: true})
      tl.fromTo(svg, {translateZ: 200}, {duration: 6, translateZ: -1500, ease: 'none'}, 0)
      tl.from(svg, {duration: 1, filter: 'blur(30px)', ease: 'none'}, 0)
      tl.from(svg, {duration: 0.5, opacity: 0, ease: 'none'}, 0)
      tl.to(svg, {duration: 1, filter: 'blur(0px)', ease: 'none'}, 1)
      tl.to(svg, {duration: 3, filter: 'blur(30px)', ease: 'none'}, 2)
      tl.to(svg, {duration: 3, opacity: 0, ease: 'none'}, 3)
      tl.add(() => {}, 16)
      const offset = (tl.duration() / svgs.length) * index
      tl.seek(offset)
      timelines.push(tl)
    })
    st = ScrollTrigger.create({
      trigger: logos,
      onEnter: play,
      onEnterBack: play,
      onLeave: pause,
      onLeaveBack: pause,
    })

    function play () {
      timelines.map(tl => tl.play())
    }
    function pause () {
      timelines.map(tl => tl.pause())
    }
  })

  onDestroy(() => {
    st?.kill()
  })

</script>

<section class="part5">
  <div class="rgrid-md-3-4">
    <div class="col">
      <AnimatedTitle class="large" style="maskup">Years ago, advertisers could reach virtually every household by placing a few linear TV network buys.</AnimatedTitle>
      <AnimatedTitle>Today, reaching your full target audience requires buyers to interact with a crowded landscape of broadcasters, SVOD services and AVOD platforms.</AnimatedTitle>
    </div>
  </div>
  <div class="logos-loop full-width" bind:this={logos}>
    <div class="logo-square logo-pos1"><LogoCW /></div>
    <div class="logo-square logo-pos2"><LogoDish /></div>
    <div class="logo-square logo-pos3"><LogoHulu /></div>
    <div class="logo-square logo-pos4"><LogoPluto /></div>
    <div class="logo-square logo-pos5"><LogoSling /></div>
    <div class="logo-square logo-pos6"><LogoFox /></div>
    <div class="logo-square logo-pos1"><LogoNBC /></div>
    <div class="logo-square logo-pos2"><LogoAMC /></div>
    <div class="logo-square logo-pos3"><LogoAE /></div>
    <div class="logo-square logo-pos4"><LogoDisney /></div>
    <div class="logo-square logo-pos5"><LogoTubi /></div>
    <div class="logo-square logo-pos6"><LogoUnivision /></div>
    <div class="logo-square logo-pos1"><LogoViacom /></div>
    <div class="logo-square logo-pos2"><LogoESPN /></div>
    <div class="logo-square logo-pos3"><LogoXumo /></div>
  </div>
  <div class="rgrid-md-3-4">
    <div class="col">
      <AnimatedTitle>In addition to linear TV, consumers now have over 300 OTT offerings to choose from, with an average of 4+ streaming services per person.</AnimatedTitle>
    </div>
  </div>
  <div class="rgrid-md-1-2 stats">
    <div class="stat stat1">
      <AnimatedBlock useChildren={true}>
        <h1 class="title">300 OTT</h1>
        <p class="subtitle"><b>Offerings to choose from</b></p>
      </AnimatedBlock>
      {#if $media.desktop}
        <ViewSource id="intro_300_ott">Lemonlight Jan 2019.</ViewSource>
      {/if}
    </div>
    <div class="stat stat2">
      <AnimatedBlock useChildren={true}>
        <h1 class="title">4+</h1>
        <p class="subtitle"><b>Streaming services per person</b></p>
      </AnimatedBlock>
      {#if $media.desktop}
        <ViewSource id="intro_4plus">OnePoll market research via LA Times Apr 2020</ViewSource>
      {/if}
    </div>
    {#if $media.mobile}
      <ViewSource id="intro_300_ott_4plus">Lemonlight Jan 2019, OnePoll market research via LA Times Apr 2020.</ViewSource>
    {/if}
  </div>
</section>

<style>
  .logos-loop {
    height: 620px;
    position: relative;
    perspective: 600px;
    margin-bottom: 80px;
  }
  .logo-square {
    position: absolute;
    width: 270px;
    height: 270px;
    background: white;
    color: black;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .logo-square :global(svg) {
    width: 69%;
    height: auto;
  }
  .logo-pos1 {
    top: 0;
    left: 0;
  }
  .logo-pos2 {
    top: 0;
    right: 0;
  }
  .logo-pos3 {
    top: 270px;
    left: 0;
  }
  .logo-pos4 {
    top: 270px;
    right: 0;
  }
  .logo-pos5 {
    bottom: 0;
    left: 0;
  }
  .logo-pos6 {
    bottom: 0;
    right: 0;
  }
  .stat {
    margin-top: 4rem;
  }
  @media(max-width: 767px) {
    .stat2 {
      margin-top: 2.5rem;
    }
  }
  .stat :global(.title) {
    font-size: 4.25rem;
    margin: 0 0 0.5rem;
  }
  .stat :global(.subtitle) {
    margin: 0;
  }
  section :global(.view-source-wrap) {
    margin-top: 0.25rem;
  }
</style>